import {SpringPageable} from "@variocube/app-ui";
import {useMemo} from "react";
import {TypeOf} from "zod";
import {useAccessToken} from "../auth";
import {useClientId} from "../ClientIdProvider";
import {useTenantId} from "../tenant";
import {BookingNameMutation, createApiClientWithBaseConfig} from "./client";
import {BookingCreation, get_QueryBookings} from "./client.gen";

export function useBookingApi() {
	const accessToken = useAccessToken();
	const tenantId = useTenantId();
	const clientId = useClientId();
	return useMemo(() => createBookingApi(tenantId, accessToken, clientId), [tenantId, accessToken]);
}

export type BookingQuery = TypeOf<get_QueryBookings["parameters"]>["query"];

function createBookingApi(tenantId: string, accessToken?: string, clientId?: string) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);
	return {
		getBooking: (bookingId: number) =>
			api.get(
				"/api/tenants/{tenantId}/bookings/{bookingId}",
				withConfig({
					path: {tenantId, bookingId},
				}),
			),
		updateBookingUntil: (bookingId: number, until: string) =>
			api.put(
				"/api/tenants/{tenantId}/bookings/{bookingId}/until",
				withConfig({
					path: {tenantId, bookingId},
					body: {until},
				}),
			),
		updateBookingName: (bookingId: number, bookingNameMutation: BookingNameMutation) =>
			api.put(
				"/api/tenants/{tenantId}/bookings/{bookingId}/name",
				withConfig({
					path: {tenantId, bookingId},
					body: bookingNameMutation,
				}),
			),
		deleteBooking: (bookingId: number) =>
			api.delete(
				"/api/tenants/{tenantId}/bookings/{bookingId}",
				withConfig({
					path: {tenantId, bookingId},
				}),
			),
		createBooking: (bookingCreation: BookingCreation) =>
			api.post(
				"/api/tenants/{tenantId}/bookings",
				withConfig({
					path: {tenantId},
					body: bookingCreation,
				}),
			),
		queryBookings: (query: BookingQuery) =>
			api.get(
				"/api/tenants/{tenantId}/bookings",
				withConfig({
					path: {tenantId},
					query,
				}),
			),
		getBookingAuditLog: (bookingId: number, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/bookings/{bookingId}/audit",
				withConfig({
					path: {tenantId, bookingId},
					query: pageable,
				}),
			),
		addBookingUser: (bookingId: number, username: string) =>
			api.post(
				"/api/tenants/{tenantId}/bookings/{bookingId}/users/{username}",
				withConfig({
					path: {tenantId, bookingId, username},
				}),
			),
		removeBookingUser: (bookingId: number, username: string) =>
			api.delete(
				"/api/tenants/{tenantId}/bookings/{bookingId}/users/{username}",
				withConfig({
					path: {tenantId, bookingId, username},
				}),
			),
	};
}
